import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { shippingApi } from './api';

// Memory only: no address/token/artifact bytes in localStorage, URLs or logs.
export default function LabelPreview({ job, revisions, csrfToken, disabled, onAccessLost, onBusy }) {
  const [history, setHistory] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(null);
  const controller = useRef(null);
  const urls = useRef([]);
  const generation = useRef(0);
  const clearPreview = useCallback(() => {
    urls.current.forEach((url) => URL.revokeObjectURL(url));
    urls.current = [];
    setPreview(null);
  }, []);
  const fail = useCallback((err) => {
    if (err.name === 'AbortError') return;
    clearPreview();
    setError(err.message || 'Label preview unavailable. Reload the package.');
    if (err.status === 401 || err.status === 403) onAccessLost(err);
  }, [clearPreview, onAccessLost]);
  const loadHistory = useCallback(async (signal) => {
    const data = await shippingApi.labels(job.id, signal);
    if (!signal.aborted) setHistory(data);
    return data;
  }, [job.id]);

  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    loadHistory(current.signal).catch((err) => { if (!current.signal.aborted) fail(err); });
    // Returning to this page requires another server check before private pixels
    // are displayed. A background tab never keeps a displayed address preview.
    const hide = () => { generation.current += 1; clearPreview(); };
    const refresh = () => {
      hide();
      if (!document.hidden) loadHistory(current.signal).catch((err) => { if (!current.signal.aborted) fail(err); });
    };
    window.addEventListener('blur', hide);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      current.abort(); generation.current += 1;
      urls.current.forEach((url) => URL.revokeObjectURL(url)); urls.current = [];
      window.removeEventListener('blur', hide); window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      onBusy(false);
    };
  }, [loadHistory, clearPreview, fail, onBusy]);

  useEffect(() => { if (disabled) clearPreview(); }, [disabled, clearPreview]);

  async function show(artifact, signal, ticket) {
    const [address, identity] = await Promise.all([
      shippingApi.labelFile(job.id, artifact.artifact_key, 'address', 'png', signal),
      shippingApi.labelFile(job.id, artifact.artifact_key, 'identity', 'png', signal),
    ]);
    if (signal.aborted || ticket !== generation.current || document.hidden) return;
    // Check again after both responses; discard a package changed during fetch.
    const latest = await loadHistory(signal);
    if (signal.aborted || ticket !== generation.current || !latest.artifacts.some((row) => row.artifact_key === artifact.artifact_key && row.available)) return;
    const addressUrl = URL.createObjectURL(address), identityUrl = URL.createObjectURL(identity);
    urls.current = [addressUrl, identityUrl];
    setPreview({ artifact, addressUrl, identityUrl });
  }

  async function generate() {
    if (busy || disabled) return;
    setBusy(true); onBusy(true); setError(''); clearPreview();
    const signal = controller.current.signal, ticket = ++generation.current;
    try {
      if (!pending.current) {
        if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs unavailable. Use a supported browser over HTTPS.');
        pending.current = { expected: revisions, request_key: Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (n) => n.toString(16).padStart(2, '0')).join('') };
      }
      const result = await shippingApi.packingCommand(`jobs/${job.id}/labels`, pending.current, csrfToken, signal);
      pending.current = null;
      if (!signal.aborted) { await loadHistory(signal); await show(result.artifact, signal, ticket); }
    } catch (err) {
      if (err.status >= 400 && err.status < 500) pending.current = null;
      if (!signal.aborted) fail(err);
    } finally {
      if (!signal.aborted) { setBusy(false); onBusy(false); }
    }
  }

  async function download(kind) {
    if (busy || !preview || disabled) return;
    setBusy(true); onBusy(true); setError('');
    const signal = controller.current.signal;
    try {
      const artifactKey = preview.artifact.artifact_key;
      const blob = await shippingApi.labelFile(job.id, artifactKey, kind, 'pdf', signal);
      if (signal.aborted) return;
      const url = URL.createObjectURL(blob);
      urls.current.push(url);
      const link = document.createElement('a');
      link.href = url; link.download = `shipping-${kind}-${artifactKey}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      // Retain only for the browser's download handoff; also revoked on unmount.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { if (!signal.aborted) fail(err); }
    finally { if (!signal.aborted) { setBusy(false); onBusy(false); } }
  }

  return <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: 'background.paper' }}><Stack spacing={1.25}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Address label</Typography>
        <Typography variant="body2" color="text.secondary">Generate the current label preview, then apply the label to this package.</Typography>
      </Box>
    </Stack>
    {error && <Alert severity="warning">{error}</Alert>}
    {history?.blockers?.length > 0 && <Alert severity="warning">Label unavailable: {history.blockers.join(', ')}. Reload and review this package.</Alert>}
    <Button size="large" variant="contained" disabled={busy || disabled} onClick={generate}>
      {pending.current ? 'Retry the same label request' : 'Generate / refresh label preview'}
    </Button>
    {preview && <>
      <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography variant="subtitle2">Current label</Typography>
        <Typography variant="caption" color="text.secondary">Manifest {preview.artifact.manifest_revision} · Address {preview.artifact.address_revision}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">Current address and package identity previews are bound to this package revision.</Typography>
      {['address', 'identity'].map((kind) => <Box key={kind}>
        <Typography variant="subtitle2">{kind === 'address' ? 'Address label' : 'Separate identity sticker · calibration preview'}</Typography>
        <Box sx={{ overflowX: 'auto', py: 1 }}><Box component="img" alt={kind === 'address' ? 'Private package address label preview' : 'Linked package identity sticker preview'}
          src={preview[`${kind}Url`]} sx={{ display: 'block', width: '90mm', height: '29mm', maxWidth: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }} /></Box>
        <Button size="small" disabled={busy || disabled} onClick={() => download(kind)}>Download {kind === 'address' ? 'address label' : 'identity sticker'} PDF</Button>
      </Box>)}
    </>}
    <Box component="details">
      <Typography component="summary" variant="body2" sx={{ cursor: 'pointer', fontWeight: 650, color: 'text.secondary', userSelect: 'none' }}>Label safety, calibration & history</Typography>
      <Stack spacing={0.75} sx={{ pt: 1 }}>
        <Typography variant="body2">Printer: Brother QL-810W · label size 29 × 90 mm{history?.package_reference ? ` · ${history.package_reference}` : ''}</Typography>
        {preview && <Typography variant="body2">Effective service: {preview.artifact.decision.effective_service} · Buyer selected: {preview.artifact.decision.buyer_selected_service || 'Unknown'}</Typography>}
        <Typography variant="body2">Preview/download only. This does not confirm a label was printed or applied. Continue the existing manual package checks.</Typography>
        <Typography variant="body2">QR stays on a separate linked identity sticker until real-size address legibility and QR quiet-zone/readability checks pass on the Brother printer. The QR cannot select a job or pair a phone.</Typography>
        <Typography variant="caption">Exact page size: 90 mm wide × 29 mm high. Screen scale varies with zoom/display; calibrate the downloaded PDF at 100%, with no fit-to-page.</Typography>
        {history?.artifacts?.length > 0 && <Stack>{history.artifacts.map((row) => <Typography variant="caption" key={row.artifact_key}>
          {row.artifact_key.slice(0, 12).toUpperCase()} · M{row.manifest_revision}/A{row.address_revision} · {row.status}{!row.available && row.status === 'CURRENT' ? ' · unavailable' : ''}
        </Typography>)}</Stack>}
      </Stack>
    </Box>
  </Stack></Paper>;
}
